import { useMemo, useState, type ReactNode } from 'react'
import { Bot, CalendarDays, CheckCircle2, FileText, GitCommitHorizontal, History, Network, UserRound, UsersRound } from 'lucide-react'
import { format, isToday, isYesterday, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import ProjectMark from '@/frontend/features/Fde/ProjectMark'
import TimelineActivityEntry from '@/frontend/features/Fde/TimelineActivityEntry'
import TimelineActivityFilters, { timelineActivityKinds } from '@/frontend/features/Fde/TimelineActivityFilters'
import { groupTimelineActivities } from '@/frontend/features/Fde/timelineActivityGroups'
import type { FdeCopy, Project, TimelineActivity, TimelineEvent } from '@/frontend/features/Fde/types'

interface Props {
  activities: TimelineActivity[]
  events: TimelineEvent[]
  project: Project | null
  copy: FdeCopy
  canManage: boolean
  onEditEvent: (event: TimelineEvent) => void
  calendar?: ReactNode
}

const activityIcons = {
  project: UsersRound,
  event: CalendarDays,
  person: UserRound,
  agent: Bot,
  task: CheckCircle2,
  ontology: Network,
  decision: GitCommitHorizontal,
  file: FileText,
  management: History,
}

const activityColors = {
  project: 'bg-slate-500',
  event: 'bg-pink-500',
  person: 'bg-emerald-500',
  agent: 'bg-violet-500',
  task: 'bg-amber-500',
  ontology: 'bg-blue-500',
  decision: 'bg-cyan-600',
  file: 'bg-slate-500',
  management: 'bg-slate-600',
}

function dayLabel(value: string, copy: FdeCopy) {
  const date = parseISO(value)
  if (isToday(date)) return copy.timeline.today
  if (isYesterday(date)) return copy.timeline.yesterday
  return format(date, "d 'de' MMM", { locale: ptBR })
}

export default function TimelineActivityFeed({ activities, events, project, copy, canManage, onEditEvent, calendar }: Props) {
  const [enabledKinds, setEnabledKinds] = useState<TimelineActivity['kind'][]>(timelineActivityKinds)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const groups = useMemo(() => groupTimelineActivities(activities), [activities])
  const filteredGroups = groups.filter((group) => {
    const activity = group.items[0]
    const date = format(parseISO(activity.occurred_at), 'yyyy-MM-dd')
    return enabledKinds.includes(activity.kind) && (!from || date >= from) && (!to || date <= to)
  })
  const filteredCount = filteredGroups.reduce((count, group) => count + group.items.length, 0)

  return (
    <div className="h-full overflow-y-auto overscroll-contain bg-muted/10 px-4 py-7 [scrollbar-gutter:stable] sm:px-8">
      <div className="mx-auto max-w-4xl">
        <div className="mb-7 flex items-start gap-4 border-b border-border pb-6">
          {project && <ProjectMark project={project} className="size-16 shrink-0 rounded-xl text-xl shadow-sm" />}
          <div className="min-w-0 pt-0.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">{copy.timeline.activity_eyebrow}</p>
            <h3 className="mt-1 truncate text-2xl font-semibold tracking-tight text-foreground">{project?.name || copy.timeline.activity_title}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{project?.description || project?.objective || copy.timeline.activity_description}</p>
          </div>
        </div>
        {calendar}
        {activities.length > 0 && <TimelineActivityFilters enabledKinds={enabledKinds} from={from} to={to} resultCount={filteredCount} totalCount={activities.length} copy={copy} onKindsChange={setEnabledKinds} onFromChange={setFrom} onToChange={setTo} onClear={() => { setEnabledKinds(timelineActivityKinds); setFrom(''); setTo('') }} />}
        {activities.length === 0 && <div className="rounded-xl border border-dashed border-border px-5 py-12 text-center text-sm text-muted-foreground">{copy.timeline.activity_empty}</div>}
        {activities.length > 0 && filteredGroups.length === 0 && <div className="rounded-xl border border-dashed border-border px-5 py-12 text-center text-sm text-muted-foreground">{copy.timeline.filter_empty}</div>}
        <ol>
          {filteredGroups.map((group, index) => {
            const item = group.items[0]
            const Icon = activityIcons[item.kind]
            const previousDay = index > 0 ? dayLabel(filteredGroups[index - 1].items[0].occurred_at, copy) : null
            const showDay = dayLabel(item.occurred_at, copy) !== previousDay
            return <li key={group.id} className="grid grid-cols-[4.5rem_1.25rem_minmax(0,1fr)] gap-x-3 sm:grid-cols-[6rem_1.5rem_minmax(0,1fr)]">
              <div className="pt-0.5 text-right text-xs font-semibold text-foreground">{showDay ? dayLabel(item.occurred_at, copy) : ''}</div>
              <div className="relative flex justify-center">
                {index < filteredGroups.length - 1 && <span className="absolute bottom-0 top-5 w-px bg-border" />}
                <span className={`relative z-10 flex size-5 items-center justify-center rounded-full text-white ring-4 ring-background sm:size-6 ${activityColors[item.kind]}`}><Icon className="size-3" /></span>
              </div>
              <TimelineActivityEntry group={group} events={events} copy={copy} canManage={canManage} onEditEvent={onEditEvent} />
            </li>
          })}
        </ol>
      </div>
    </div>
  )
}
